// pageContent.js - Content loader for all landing pages
// Imports individual page content files and provides unified access

// Import Tier 1 Educational Content
import aepRetentionPlaybook from './pages/aep-retention-playbook';
import bookOfBusinessRetention from './pages/book-of-business-retention';
import medigapRetentionStrategies from './pages/medigap-retention-strategies';
import oepRetentionStrategy from './pages/oep-retention-strategy';
import dualSnpRetentionStrategies from './pages/dual-snp-retention-strategies';
import yearRoundRetention from './pages/year-round-retention';
import maVsMedigapSwitching from './pages/ma-vs-medigap-switching';
import commissionProtection from './pages/commission-protection';
import formularyChangeAlerts from './pages/formulary-change-alerts';
import starRatingsRetention from './pages/star-ratings-retention';
import communicationWorkflows from './pages/communication-workflows';
import onboardingRetention from './pages/onboarding-retention';
import medSuppCancellationReasons from './pages/med-supp-cancellation-reasons';
import premiumIncreaseRetention from './pages/premium-increase-retention';
import networkChangeAlerts from './pages/network-change-alerts';
import smallAgencyRetention from './pages/small-agency-retention';
import largeAgencyRetention from './pages/large-agency-retention';
import crossSellingRetention from './pages/cross-selling-retention';
import complianceFriendlyOutreach from './pages/compliance-friendly-outreach';
import agentRetentionTraining from './pages/agent-retention-training';
import ppoVsHmoRetention from './pages/ppo-vs-hmo-retention';
import memberSatisfactionRetention from './pages/member-satisfaction-retention';
import policyRenewalManagement from './pages/policy-renewal-management';
import chronicConditionRetention from './pages/chronic-condition-retention';
import snpRetentionStrategies from './pages/snp-retention-strategies';

// Import Tier 2 Data & Research Content
import mapdSwitchingStatistics from './pages/mapd-switching-statistics';
import mapdChurnAnalytics from './pages/mapd-churn-analytics';
import retentionBenchmarks from './pages/retention-benchmarks';
import churnPrediction from './pages/churn-prediction';
import riskScoring from './pages/risk-scoring';
import retentionDashboard from './pages/retention-dashboard';
import maDisenrollmentAnalysis from './pages/ma-disenrollment-analysis';
import planChangeMonitoring from './pages/plan-change-monitoring';
import retentionAnalyticsReporting from './pages/retention-analytics-reporting';
import agencyRetentionSystem from './pages/agency-retention-system';
import persistencyImprovement from './pages/persistency-improvement';
import bookValuationRetention from './pages/book-valuation-retention';
import leadVsRetentionRoi from './pages/lead-vs-retention-roi';
import agentVsClientChurn from './pages/agent-vs-client-churn';
import callCenterRetention from './pages/call-center-retention';
import retentionCaseStudies from './pages/retention-case-studies';

// Import Tier 3 Solution Content
import crmForRetention from './pages/crm-for-retention';
import clientRetentionSoftware from './pages/client-retention-software';
import aiRetentionPlatform from './pages/ai-retention-platform';
import outreachAutomation from './pages/outreach-automation';
import agentRetentionPlatform from './pages/agent-retention-platform';
import maRetentionTool from './pages/ma-retention-tool';
import fmoRetentionSoftware from './pages/fmo-retention-software';
import annualReviewAutomation from './pages/annual-review-automation';
import clientEngagementSystem from './pages/client-engagement-system';
import lapsePreventionSoftware from './pages/lapse-prevention-software';

// Map slugs to their content
const PAGE_CONTENT = {
  // Tier 1 Educational
  'aep-retention-playbook': aepRetentionPlaybook,
  'book-of-business-retention': bookOfBusinessRetention,
  'medigap-retention-strategies': medigapRetentionStrategies,
  'oep-retention-strategy': oepRetentionStrategy,
  'dual-snp-retention-strategies': dualSnpRetentionStrategies,
  'year-round-retention': yearRoundRetention,
  'ma-vs-medigap-switching': maVsMedigapSwitching,
  'commission-protection': commissionProtection,
  'formulary-change-alerts': formularyChangeAlerts,
  'star-ratings-retention': starRatingsRetention,
  'communication-workflows': communicationWorkflows,
  'onboarding-retention': onboardingRetention,
  'med-supp-cancellation-reasons': medSuppCancellationReasons,
  'premium-increase-retention': premiumIncreaseRetention,
  'network-change-alerts': networkChangeAlerts,
  'small-agency-retention': smallAgencyRetention,
  'large-agency-retention': largeAgencyRetention,
  'cross-selling-retention': crossSellingRetention,
  'compliance-friendly-outreach': complianceFriendlyOutreach,
  'agent-retention-training': agentRetentionTraining,
  'ppo-vs-hmo-retention': ppoVsHmoRetention,
  'member-satisfaction-retention': memberSatisfactionRetention,
  'policy-renewal-management': policyRenewalManagement,
  'chronic-condition-retention': chronicConditionRetention,
  'snp-retention-strategies': snpRetentionStrategies,

  // Tier 2 Data & Research
  'mapd-switching-statistics': mapdSwitchingStatistics,
  'mapd-churn-analytics': mapdChurnAnalytics,
  'retention-benchmarks': retentionBenchmarks,
  'churn-prediction': churnPrediction,
  'risk-scoring': riskScoring,
  'retention-dashboard': retentionDashboard,
  'ma-disenrollment-analysis': maDisenrollmentAnalysis,
  'plan-change-monitoring': planChangeMonitoring,
  'retention-analytics-reporting': retentionAnalyticsReporting,
  'agency-retention-system': agencyRetentionSystem,
  'persistency-improvement': persistencyImprovement,
  'book-valuation-retention': bookValuationRetention,
  'lead-vs-retention-roi': leadVsRetentionRoi,
  'agent-vs-client-churn': agentVsClientChurn,
  'call-center-retention': callCenterRetention,
  'retention-case-studies': retentionCaseStudies,

  // Tier 3 Solutions
  'crm-for-retention': crmForRetention,
  'client-retention-software': clientRetentionSoftware,
  'ai-retention-platform': aiRetentionPlatform,
  'outreach-automation': outreachAutomation,
  'agent-retention-platform': agentRetentionPlatform,
  'ma-retention-tool': maRetentionTool,
  'fmo-retention-software': fmoRetentionSoftware,
  'annual-review-automation': annualReviewAutomation,
  'client-engagement-system': clientEngagementSystem,
  'lapse-prevention-software': lapsePreventionSoftware,
};

export function getPageContent(slug) {
  return PAGE_CONTENT[slug] || null;
}

export function hasPageContent(slug) {
  return slug in PAGE_CONTENT;
}

export function getAllContentSlugs() {
  return Object.keys(PAGE_CONTENT);
}

// Get content by tier for sitemap generation
export function getContentByTier() {
  return {
    tier1: [
      'aep-retention-playbook',
      'book-of-business-retention',
      'medigap-retention-strategies',
      'oep-retention-strategy',
      'dual-snp-retention-strategies',
      'year-round-retention',
      'ma-vs-medigap-switching',
      'commission-protection',
      'formulary-change-alerts',
      'star-ratings-retention',
      'communication-workflows',
      'onboarding-retention',
      'med-supp-cancellation-reasons',
      'premium-increase-retention',
      'network-change-alerts',
      'small-agency-retention',
      'large-agency-retention',
      'cross-selling-retention',
      'compliance-friendly-outreach',
      'agent-retention-training',
      'ppo-vs-hmo-retention',
      'member-satisfaction-retention',
      'policy-renewal-management',
      'chronic-condition-retention',
      'snp-retention-strategies',
    ],
    tier2: [
      'mapd-switching-statistics',
      'mapd-churn-analytics',
      'retention-benchmarks',
      'churn-prediction',
      'risk-scoring',
      'retention-dashboard',
      'ma-disenrollment-analysis',
      'plan-change-monitoring',
      'retention-analytics-reporting',
      'agency-retention-system',
      'persistency-improvement',
      'book-valuation-retention',
      'lead-vs-retention-roi',
      'agent-vs-client-churn',
      'call-center-retention',
      'retention-case-studies',
    ],
    tier3: [
      'crm-for-retention',
      'client-retention-software',
      'ai-retention-platform',
      'outreach-automation',
      'agent-retention-platform',
      'ma-retention-tool',
      'fmo-retention-software',
      'annual-review-automation',
      'client-engagement-system',
      'lapse-prevention-software',
    ],
  };
}

export default PAGE_CONTENT;
